import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useFont } from "../../contexts/FontContext/FontContext";
import BackButton from "../BackButton/BackButton";
import LogoWrapper from "../LogoWrapper/LogoWrapper";
import TextInput from "../TextInput/TextInput";
import styles from "./PostOfficeOption.module.css";

const PostOfficeOption = ( { logoUrl } ) => {
    const [businessName, setBusinessName] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [streetOne, setStreetOne] = useState("");
    const [streetTwo, setStreetTwo] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [country, setCountry] = useState("");
    const [error, setError] = useState(null);
    const [trackingNumber, setTrackingNumber] = useState("");
    const [selectedCarrier, setSelectedCarrier] = useState(null); // Default to USPS
    const fontSchemes = useFont();
    const fontFamilyOne = fontSchemes.scheme_one;
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get("token");

    useEffect(() => {
        const fetchBusinessDetails = async () => {
            const apiUrl = process.env.REACT_APP_API_URL;
            try {
                const response = await fetch(`${apiUrl}/api/return_details/${token}`, {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                });
                if (!response.ok) throw new Error("Failed to fetch business details");
                const data = await response.json();
                setBusinessName(data.business_name);
                setCompanyName(data.company_name);
                setStreetOne(data.street1);
                setStreetTwo(data.street2);
                setCity(data.city);
                setState(data.state);
                setZipCode(data.zip_code);
                setCountry(data.country);
            } catch (err) { setError(err.message); }
        };
        fetchBusinessDetails();
    }, [token]);

    const carriers = [
        { id: "usps", logo: `${process.env.PUBLIC_URL}/images/usps_logo.png` },
        { id: "fedex", logo: `${process.env.PUBLIC_URL}/images/fedex_logo.png` },
        { id: "ups", logo: `${process.env.PUBLIC_URL}/images/ups_logo.png` },
        { id: "dhl", logo: `${process.env.PUBLIC_URL}/images/dhl_logo.png` },
    ];

    const handleCarrierSelect = (carrierId) => {
        setSelectedCarrier(carrierId);
        const carrierLinks = {
            usps: "https://tools.usps.com/go/TrackConfirmAction_input",
            fedex: "https://www.fedex.com/en-us/tracking.html",
            dhl: "https://www.dhl.com/us-en/home/tracking.html",
            ups: "https://www.ups.com/track?loc=en_US&requester=ST",
        };
        const url = carrierLinks[carrierId];
        window.open(url, "_blank");
    };

    return (
        <div className={styles.post_office_option_container}>
            <Helmet>
                <title>Go to post office</title>
            </Helmet>
            <LogoWrapper class_={styles.post_office_option_logo} src={logoUrl ? `${process.env.PUBLIC_URL}/business_images/${logoUrl}` : `${process.env.PUBLIC_URL}/images/partial_logo.png`} />
            <div className={`${styles.send_return_text_wrapper} ${styles.text_wrappers}`}>
                <p className={`titles ${styles.send_return_text} ${fontFamilyOne}`}>Please send return to <strong>{businessName}'s</strong> warehouse.</p>    
            </div>
            <div className={`${styles.business_address_wrapper} ${styles.text_wrappers}`}>
                {companyName && (<p className={`titles ${styles.business_address} ${fontFamilyOne}`}>{`${companyName} ATTN: ${businessName}`}</p>)}
                <p className={`titles ${styles.business_address} ${fontFamilyOne}`}>{`${streetOne} ${streetTwo ? `, ${streetTwo}` : ""}, ${city}, ${state} ${zipCode}, ${country}`}</p>
            </div>
            <div className={`${styles.post_office_blurbs} ${styles.post_office_blurb_wrapper}`}>
                <p className={`${styles.post_office_blurb} ${fontFamilyOne}`}> Refunds will be processed once we receive your package, typically within 5-10 business days.</p>
            </div>
            <div className={`${styles.post_office_blurbs} ${styles.retrieve_tracking_blurb_wrapper}`}>
                <p className={`${styles.retrieve_tracking_blurb} ${fontFamilyOne}`}>Retrieve tracking number from post office and search by carrier.</p>       
            </div>
            <div className={styles.tracking_number_wrapper}>
                <div className={styles.carrier_select_container}>
                    {carriers.map((carrier) => (
                        <img key={carrier.id} src={carrier.logo} className={`${styles.carrier_logos}`} onClick={() => handleCarrierSelect(carrier.id)} onMouseEnter={(e) => { if (selectedCarrier !== carrier.id) e.target.classList.add(styles.carrier_highlighted); }} onMouseLeave={(e) => { if (selectedCarrier !== carrier.id) e.target.classList.remove(styles.carrier_highlighted); }} alt={`${carrier.id} logo`} />
                    ))}
                </div>
            </div>
            <BackButton button_id={styles.post_office_back_button} image_id={styles.post_office_back_button_arrow} />
        </div>
    );
};

export default PostOfficeOption;